// frontend/src/pages/NotFound.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faArrowLeft,
  faSeedling,
  faTractor,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../components/UI/Button";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// MAIN COMPONENT
// ============================================
const NotFound = () => {
  return (
    <motion.div
      className="not-found-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="not-found-container">
        <motion.div
          className="not-found-content"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="not-found-icon">🌾</div>
          <h1 className="not-found-number">404</h1>
          <h2 className="not-found-title">Page Not Found</h2>
          <p className="not-found-description">
            Oops! The page you are looking for doesn't exist or has been moved.
            Let us help you find your way back.
          </p>

          <div className="not-found-actions">
            <Button href="/" variant="primary" size="large">
              <FontAwesomeIcon icon={faHome} /> Return Home
            </Button>
            <Button href="/products" variant="secondary" size="large">
              <FontAwesomeIcon icon={faTractor} /> Browse Products
            </Button>
            <Button href="/contact" variant="outline" size="large">
              <FontAwesomeIcon icon={faSearch} /> Contact Support
            </Button>
          </div>

          <div className="not-found-help">
            <a
              href="https://wa.me/23231030808"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-help"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> Need help? WhatsApp us
            </a>
            <Link to="/" className="home-link">
              <FontAwesomeIcon icon={faArrowLeft} /> Go back home
            </Link>
          </div>

          <div className="not-found-footer">
            <FontAwesomeIcon icon={faSeedling} />
            <span>Kenali Enterprise — Nutrition • Growth • Prosperity</span>
          </div>
        </motion.div>
      </div>

      <style>{`
        .not-found-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F8F8F8;
          padding: 2rem 1.5rem;
        }

        .not-found-container {
          max-width: 600px;
          width: 100%;
          margin: 0 auto;
        }

        .not-found-content {
          text-align: center;
          background: #ffffff;
          padding: 3rem 2rem;
          border-radius: 16px;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08);
          border: 1px solid #e5e5e5;
        }

        @media (min-width: 768px) {
          .not-found-content {
            padding: 4rem 3rem;
          }
        }

        .not-found-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        @media (min-width: 768px) {
          .not-found-icon {
            font-size: 4rem;
            margin-bottom: 1.5rem;
          }
        }

        .not-found-number {
          font-size: 5rem;
          font-weight: 800;
          color: #D9A321;
          font-family: "Montserrat", sans-serif;
          line-height: 1;
          margin-bottom: 0.5rem;
        }

        @media (min-width: 768px) {
          .not-found-number {
            font-size: 7rem;
          }
        }

        .not-found-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #006B2E;
          margin-bottom: 0.75rem;
          font-family: "Montserrat", sans-serif;
        }

        @media (min-width: 768px) {
          .not-found-title {
            font-size: 2rem;
          }
        }

        .not-found-description {
          color: #5A5A5A;
          font-size: 0.938rem;
          line-height: 1.7;
          max-width: 450px;
          margin: 0 auto 2rem;
        }

        @media (min-width: 768px) {
          .not-found-description {
            font-size: 1rem;
          }
        }

        .not-found-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 2rem;
        }

        @media (min-width: 480px) {
          .not-found-actions {
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
          }
        }

        .not-found-actions .btn {
          min-width: 160px;
          justify-content: center;
        }

        @media (max-width: 479px) {
          .not-found-actions .btn {
            width: 100%;
          }
        }

        .not-found-help {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          align-items: center;
          padding-top: 1.5rem;
          border-top: 1px solid #e5e5e5;
        }

        @media (min-width: 480px) {
          .not-found-help {
            flex-direction: row;
            justify-content: center;
            gap: 1.5rem;
          }
        }

        .whatsapp-help {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #25d366;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }

        .whatsapp-help:hover {
          color: #1da851;
          transform: translateY(-2px);
        }

        .home-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #D9A321;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }

        .home-link:hover {
          color: #B8860B;
          transform: translateX(-4px);
        }

        .not-found-footer {
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.7rem;
          color: #5A5A5A;
        }

        .not-found-footer svg {
          color: #D9A321;
        }

        @media (max-width: 480px) {
          .not-found-content {
            padding: 2rem 1.25rem;
          }

          .not-found-number {
            font-size: 4rem;
          }

          .not-found-title {
            font-size: 1.25rem;
          }

          .not-found-description {
            font-size: 0.875rem;
          }

          .not-found-help {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default NotFound;
